<!-- ============================================================
     AI Form Designer — Import wizard (mockup v2)
     Upload → Preview → Complete, matching AI_Form_Builder_Mockups_v2.
     ============================================================ -->
<div class="ifw-page" *ngIf="isVisible">
  <app-header></app-header>

  <!-- Full-screen blocking overlay while the AI analyzes the uploaded file -->
  <div class="ifw-analyzing-overlay" *ngIf="isAnalyzing">
    <div class="ifw-analyzing-box">
      <img class="ifw-logo-spin ifw-logo-lg" src="favicon.ico" alt="">
      <div class="ifw-analyzing-text">Analyzing with AI…</div>
      <div class="ifw-analyzing-sub">Reading your document and building the form. This can take a moment.</div>
    </div>
  </div>

  <div class="ifw-modal">

    <!-- ===== Blue banner header + stepper ===== -->
    <div class="ifw-header">
      <div class="ifw-header-top">
        <div class="ifw-title">
          <span class="ifw-title-ic"><i [class]="editMode ? 'fas fa-robot' : 'fas fa-file-download'"></i></span>
          <span class="ifw-title-text">{{ editMode ? 'Edit Form with AI' : 'Import Form' }}</span>
          <span class="ifw-title-form" *ngIf="formName">— {{ formName }}</span>
        </div>
        <button class="ifw-close" *ngIf="currentStep !== 'complete'" (click)="close()" aria-label="Close"><i class="fas fa-times"></i></button>
      </div>

      <div class="ifw-steps">
        <div class="ifw-step"
             [class.active]="currentStep === 'upload'"
             [class.done]="currentStep !== 'upload'">
          <span class="ifw-step-dot"><i *ngIf="currentStep !== 'upload'" class="fas fa-check"></i><span *ngIf="currentStep === 'upload'">1</span></span>
          <span class="ifw-step-name">Upload</span>
        </div>
        <span class="ifw-step-line" [class.done]="currentStep !== 'upload'"></span>
        <div class="ifw-step"
             [class.active]="currentStep === 'preview'"
             [class.done]="currentStep === 'importing' || currentStep === 'complete'">
          <span class="ifw-step-dot"><i *ngIf="currentStep === 'importing' || currentStep === 'complete'" class="fas fa-check"></i><span *ngIf="currentStep !== 'importing' && currentStep !== 'complete'">2</span></span>
          <span class="ifw-step-name">Preview</span>
        </div>
        <span class="ifw-step-line" [class.done]="currentStep === 'importing' || currentStep === 'complete'"></span>
        <div class="ifw-step"
             [class.active]="currentStep === 'importing' || currentStep === 'complete'"
             [class.done]="currentStep === 'complete'">
          <span class="ifw-step-dot"><i *ngIf="currentStep === 'complete'" class="fas fa-check"></i><span *ngIf="currentStep !== 'complete'">3</span></span>
          <span class="ifw-step-name">Complete</span>
        </div>
      </div>
    </div>

    <!-- ===== Body ===== -->
    <div class="ifw-body">

      <!-- ---------- STEP 1: UPLOAD ---------- -->
      <section *ngIf="currentStep === 'upload'" class="ifw-upload">

        <div class="ifw-ai-badge-row">
          <span class="ifw-ai-badge"><i class="fas fa-plus"></i> AI-POWERED</span>
        </div>

        <!-- Edit the already-saved form with the Form Assistant (no re-upload) -->
        <div class="ifw-edit-existing" *ngIf="hasExistingForm && aiAssistantEnabled">
          <div>
            <strong><i class="fas fa-robot"></i> Already imported this form?</strong>
            <p>Edit it with the Form Assistant — no need to re-upload.</p>
          </div>
          <button type="button" class="ifw-btn-ghost" (click)="editCurrentForm()">Edit current form with AI</button>
        </div>

        <div class="ifw-upload-grid">
          <!-- Drop card -->
          <div class="ifw-card ifw-drop-card">
            <div class="ifw-dropzone"
                 [class.drag-over]="isDragOver"
                 (dragover)="onDragOver($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onDrop($event)">
              <div class="ifw-drop-cloud"><i class="fas fa-cloud-upload-alt"></i></div>
              <div class="ifw-drop-title">Drag &amp; Drop File Here</div>
              <div class="ifw-drop-or">or</div>
              <label class="ifw-browse">
                <input type="file" accept=".xlsx,.xls,.xlsm,.docx,.doc,.pdf,.csv,.txt" (change)="onFileSelected($event)" hidden>
                <i class="fas fa-file"></i> Browse Files
              </label>
              <div class="ifw-supported">
                Supported: <b>.pdf .docx .doc .xlsx .xls .xlsm .csv .txt</b>
                &nbsp;·&nbsp; Max file size <b>25 MB</b>
              </div>
            </div>

            <div *ngIf="uploadError" class="ifw-error" role="alert">
              <i class="fas fa-exclamation-circle"></i> {{ uploadError }}
            </div>
          </div>

          <!-- Guidance card -->
          <div class="ifw-card ifw-guidance-card">
            <div class="ifw-guidance-head"><i class="fas fa-info-circle"></i> Formatting Guidance</div>

            <div class="ifw-guide-item">
              <div class="ifw-guide-ic excel"><i class="fas fa-table"></i></div>
              <div>
                <div class="ifw-guide-title">Structured</div>
                <div class="ifw-guide-desc">Columns: Name, Label, Type, Required, Options, Section, HelpText, Condition. Example: <code>IF Q4 = Yes</code></div>
              </div>
            </div>

            <div class="ifw-guide-item">
              <div class="ifw-guide-ic word"><i class="fas fa-file-alt"></i></div>
              <div>
                <div class="ifw-guide-title">Free-Form</div>
                <div class="ifw-guide-desc">Numbered questions (e.g. "1. Company Name"), bold section headers. Conditional logic auto-detected.</div>
              </div>
            </div>

            <div class="ifw-ai-note">
              <i class="fas fa-wand-magic-sparkles"></i>
              <span>You can upload a <b>structured</b> or <b>unstructured</b> document. Our AI will analyze the content and automatically build out sections and fields for you to review in the next step.</span>
            </div>

          </div>
        </div>
      </section>

      <!-- ---------- STEP 2: PREVIEW ---------- -->
      <section *ngIf="currentStep === 'preview'" class="ifw-preview">

        <!-- File chip -->
        <div class="ifw-file-chip">
          <div class="ifw-file-ic"><i [class]="fileIconClass"></i></div>
          <div class="ifw-file-meta">
            <div class="ifw-file-name">{{ editMode ? 'Editing your current form' : selectedFile?.name }}</div>
            <div class="ifw-file-sub" *ngIf="!editMode">Uploaded just now · {{ fileSizeText }}</div>
          </div>
          <button class="ifw-change-file" (click)="goBack()">{{ editMode ? 'Back' : 'Change File' }}</button>
        </div>

        <!-- Stat tiles -->
        <div class="ifw-tiles">
          <div class="ifw-tile">
            <div class="ifw-tile-ic sections"><i class="fas fa-layer-group"></i></div>
            <div><div class="ifw-tile-num">{{ getSectionCount() }}</div><div class="ifw-tile-label">SECTIONS</div></div>
          </div>
          <div class="ifw-tile">
            <div class="ifw-tile-ic fields"><i class="fas fa-list-ul"></i></div>
            <div><div class="ifw-tile-num">{{ getTotalFields() }}</div><div class="ifw-tile-label">FIELDS</div></div>
          </div>
          <div class="ifw-tile">
            <div class="ifw-tile-ic rules"><i class="fas fa-code-branch"></i></div>
            <div><div class="ifw-tile-num">{{ getConditionalRuleCount() }}</div><div class="ifw-tile-label">CONDITIONAL RULE{{ getConditionalRuleCount() === 1 ? '' : 'S' }}</div></div>
          </div>
        </div>

        <div class="ifw-preview-grid" [class.no-assistant]="!(aiMode && aiAssistantEnabled)">
          <!-- Sections list -->
          <div class="ifw-preview-main">
            <div class="ifw-sections-head">{{ editMode ? 'Current form sections' : 'Sections to be created' }}</div>

            <div class="ifw-section" *ngFor="let section of parseResult?.sections; let si = index">
              <div class="ifw-section-head">
                <span class="ifw-sec-num">{{ si + 1 }}</span>
                <span class="ifw-sec-title">{{ section.title }}</span>
                <span class="ifw-sec-count">{{ section.fields.length }} field{{ section.fields.length === 1 ? '' : 's' }}</span>
              </div>

              <div class="ifw-field" *ngFor="let field of section.fields; let fi = index" [class.open]="isExpanded(si, fi)">
                <div class="ifw-field-row" (click)="toggleField(si, fi)">
                  <span class="ifw-field-info" [title]="fieldQuestion(field) || displayLabel(field)"><i class="fas fa-info-circle"></i></span>
                  <span class="ifw-field-type" [ngClass]="'ifw-t-' + typeBadge(field).toLowerCase()">{{ typeBadge(field) }}</span>
                  <span class="ifw-field-label">{{ displayLabel(field) }}<span class="ifw-field-req" *ngIf="field.required"> *</span></span>
                  <span class="ifw-field-caret" *ngIf="fieldQuestion(field)"><i class="fas fa-chevron-down"></i></span>
                </div>
                <div class="ifw-field-q" *ngIf="isExpanded(si, fi) && fieldQuestion(field)">
                  <div class="ifw-field-q-label">QUESTION ON FORM</div>
                  <div class="ifw-field-q-text">“{{ fieldQuestion(field) }}”</div>
                </div>
              </div>
            </div>

            <!-- Warnings ("Heads up") -->
            <div class="ifw-heads-up" *ngIf="parseResult?.warnings?.length">
              <div class="ifw-hu-title"><i class="fas fa-exclamation-triangle"></i> Heads up</div>
              <div class="ifw-hu-body">
                <span *ngFor="let w of parseResult?.warnings">{{ w }} </span>
              </div>
            </div>

            <!-- Editable-after-import reassurance -->
            <div class="ifw-editnote">
              <i class="fas fa-info-circle"></i>
              <div>
                <strong>You can still make changes after import</strong>
                <p>Importing doesn’t lock the form. Once it’s created, you’ll be able to review, edit, and adjust sections, fields, and conditional rules anytime.</p>
              </div>
            </div>
          </div>

          <!-- Form Assistant (Edit Agent) -->
          <div class="ifw-assistant" *ngIf="aiMode && aiAssistantEnabled">
            <div class="ifw-assistant-head">
              <span class="ifw-assistant-badge"><i class="fas fa-robot"></i></span>
              <div>
                <div class="ifw-assistant-title">Form Assistant</div>
                <div class="ifw-assistant-sub">AI-powered by Exostar</div>
              </div>
            </div>

            <div class="ifw-assistant-body">
              <div class="ifw-assistant-hint" *ngIf="!chatMessages.length && !isEditing">
                Tell the assistant what to change and it will update the mapping instantly.
                <div class="ifw-assistant-eg">“Make Q3 required and add a CAGE code field”</div>
              </div>

              <div class="ifw-msg"
                   *ngFor="let m of chatMessages"
                   [class.user]="m.role === 'user'"
                   [class.assistant]="m.role === 'assistant'">
                {{ m.text }}
              </div>
              <div class="ifw-msg assistant typing" *ngIf="isEditing">Applying your change…</div>

              <!-- Save-as-a-rule prompt -->
              <div class="ifw-rule" *ngIf="ruleSuggestion && !isEditing">
                <span>Save “{{ ruleSuggestion }}” as a rule for future forms?</span>
                <label class="ifw-rule-scope">
                  <input type="checkbox" [(ngModel)]="ruleGlobal">
                  <span>Apply to <strong>all my forms</strong></span>
                </label>
                <div class="ifw-rule-actions">
                  <button type="button" class="ifw-rule-save" (click)="saveRule()">Save rule</button>
                  <button type="button" class="ifw-rule-dismiss" (click)="dismissRule()">Not now</button>
                </div>
              </div>
              <div class="ifw-rule-saved" *ngIf="ruleSavedMessage">{{ ruleSavedMessage }}</div>
            </div>

            <div class="ifw-assistant-input">
              <input type="text"
                     [(ngModel)]="chatInput"
                     (keydown)="onChatKeydown($event)"
                     [disabled]="isEditing"
                     placeholder="Tell the AI what to change">
              <button type="button" (click)="sendInstruction()" [disabled]="isEditing || !chatInput.trim()" aria-label="Send">
                <i class="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ---------- STEP 3a: BUILDING ---------- -->
      <section *ngIf="currentStep === 'importing'" class="ifw-building">
        <div class="ifw-building-spin">
          <img class="ifw-logo-spin ifw-logo-lg" src="favicon.ico" alt="Exostar">
        </div>
        <div class="ifw-build-title">{{ editMode ? 'Saving changes…' : 'Creating your form…' }}</div>
        <div class="ifw-build-sub" *ngIf="!editMode">Building {{ importProgress?.currentSection || 0 }} of {{ getSectionCount() }} sections</div>

        <div class="ifw-build-bar"><div class="ifw-build-fill" [style.width.%]="getProgressPercentage()"></div></div>
        <div class="ifw-build-pct">{{ getProgressPercentage() }}% COMPLETE</div>

        <div class="ifw-build-list" *ngIf="!editMode">
          <div class="ifw-build-item"
               *ngFor="let s of parseResult?.sections; let i = index"
               [class.done]="i < (importProgress?.currentSection || 0)"
               [class.active]="i === (importProgress?.currentSection || 0)">
            <span class="ifw-build-ic">
              <i *ngIf="i < (importProgress?.currentSection || 0)" class="fas fa-check"></i>
              <i *ngIf="i === (importProgress?.currentSection || 0)" class="fas fa-circle-notch fa-spin"></i>
              <span *ngIf="i > (importProgress?.currentSection || 0)">{{ i + 1 }}</span>
            </span>
            <span>{{ i < (importProgress?.currentSection || 0) ? 'Created' : (i === (importProgress?.currentSection || 0) ? 'Creating' : 'Create') }} section — {{ s.title }} ({{ s.fields.length }} fields)</span>
          </div>
          <div class="ifw-build-item pending" *ngIf="getConditionalRuleCount() > 0">
            <span class="ifw-build-ic"><i class="fas fa-code-branch"></i></span>
            <span>Applying {{ getConditionalRuleCount() }} conditional rule{{ getConditionalRuleCount() === 1 ? '' : 's' }}</span>
          </div>
        </div>
      </section>

      <!-- ---------- STEP 3b: SUCCESS ---------- -->
      <section *ngIf="currentStep === 'complete'" class="ifw-success">
        <div class="ifw-success-check" [class.partial]="importErrors.length > 0">
          <i [class]="importErrors.length === 0 ? 'fas fa-check' : 'fas fa-exclamation'"></i>
        </div>
        <div class="ifw-success-title">
          {{ importErrors.length === 0 ? (editMode ? 'Changes Saved!' : 'Import Successful!') : 'Import Completed with Errors' }}
        </div>
        <div class="ifw-success-sub">
          Created {{ sectionsCreated }} section{{ sectionsCreated === 1 ? '' : 's' }} with {{ fieldsCreated }} field{{ fieldsCreated === 1 ? '' : 's' }}<span *ngIf="conditionalRulesCreated > 0"> and {{ conditionalRulesCreated }} conditional rule{{ conditionalRulesCreated === 1 ? '' : 's' }}</span>.<br>
          Your form is ready to review and edit in the Form Editor.
        </div>

        <div class="ifw-tiles center">
          <div class="ifw-tile"><div><div class="ifw-tile-num">{{ sectionsCreated }}</div><div class="ifw-tile-label">SECTIONS</div></div></div>
          <div class="ifw-tile"><div><div class="ifw-tile-num">{{ fieldsCreated }}</div><div class="ifw-tile-label">FIELDS</div></div></div>
          <div class="ifw-tile"><div><div class="ifw-tile-num">{{ conditionalRulesCreated }}</div><div class="ifw-tile-label">CONDITIONAL RULE{{ conditionalRulesCreated === 1 ? '' : 'S' }}</div></div></div>
        </div>

        <div *ngIf="importErrors.length > 0" class="ifw-error-list">
          <h4>Errors</h4>
          <ul><li *ngFor="let error of importErrors">{{ error }}</li></ul>
        </div>

        <div class="ifw-success-actions">
          <button class="ifw-btn-primary" (click)="goToFormEditor()">
            <i class="fas fa-arrow-right"></i> Done — Go to Form Editor
          </button>
        </div>
      </section>
    </div>

    <!-- ===== Footer (Upload / Preview only) ===== -->
    <div class="ifw-footer" *ngIf="currentStep === 'upload' || currentStep === 'preview'">
      <button class="ifw-btn-secondary" (click)="close()">Cancel</button>
      <button class="ifw-btn-primary"
              *ngIf="currentStep === 'preview' && !editMode"
              (click)="startImport()"
              [disabled]="!templateId">
        <i class="fas fa-file-download"></i> Import Form
      </button>
      <button class="ifw-btn-primary" *ngIf="currentStep === 'preview' && editMode" (click)="close()">Done</button>
    </div>

  </div>
</div>
